import React from 'react';

interface HintPopupProps {
  show: boolean;
  title?: string;
  description?: React.ReactNode;
  image?: string;
  imageAlt?: string;
  onClose: () => void;
  showCheckbox?: boolean;
  checkboxValue?: boolean;
  onCheckboxChange?: (checked: boolean) => void;
  checkboxLabel?: string;
  closeButtonText?: string;
  maxWidth?: string;
  // 実行ボタン関連
  onExecute?: () => void;
  executeButtonText?: string;
  showExecuteButton?: boolean;
}

/**
 * 汎用ヒントポップアップコンポーネント
 *
 * AI加工機能の説明やワンルーム注意事項など、
 * 様々な情報表示ポップアップに使用できます。
 *
 * 使用例1: テキストヒント（AI青空加工）
 * ```tsx
 * <HintPopup
 *   show={showHint}
 *   title="AI青空加工とは？"
 *   description={
 *     <div className="space-y-3">
 *       <p><strong>機能：</strong>写真の空を自動検出し、美しい青空に変換します。</p>
 *       <ul className="list-disc list-inside space-y-1 ml-2">
 *         <li>曇り空を晴天の青空に変えたい</li>
 *         <li>物件写真の印象を明るくしたい</li>
 *       </ul>
 *     </div>
 *   }
 *   onClose={handleClose}
 *   showCheckbox={true}
 *   checkboxValue={dontShowAgain}
 *   onCheckboxChange={setDontShowAgain}
 * />
 * ```
 *
 * 使用例2: 画像ヒント（ワンルーム注意事項）
 * ```tsx
 * <HintPopup
 *   show={showNotice}
 *   image={oneRoomNoticeImage}
 *   imageAlt="ワンルームにおける入力画像の注意点"
 *   onClose={handleClose}
 *   showCheckbox={true}
 *   checkboxValue={dontShowAgain}
 *   onCheckboxChange={setDontShowAgain}
 *   maxWidth="1000px"
 * />
 * ```
 *
 * @param show - ポップアップの表示/非表示
 * @param title - タイトル（任意）
 * @param description - 説明文（ReactNode、任意）
 * @param image - 画像URL（任意）
 * @param imageAlt - 画像の代替テキスト（任意）
 * @param onClose - 閉じるボタンのクリックハンドラ
 * @param showCheckbox - チェックボックスの表示/非表示（デフォルト: false）
 * @param checkboxValue - チェックボックスの状態（任意）
 * @param onCheckboxChange - チェックボックス変更ハンドラ（任意）
 * @param checkboxLabel - チェックボックスのラベル（デフォルト: '次回から表示しない'）
 * @param closeButtonText - 閉じるボタンのテキスト（デフォルト: '閉じる'）
 * @param maxWidth - モーダルの最大幅（デフォルト: '600px'）
 * @param onExecute - 実行ボタンのクリックハンドラ（任意）
 * @param executeButtonText - 実行ボタンのテキスト（デフォルト: '適用'）
 * @param showExecuteButton - 実行ボタンの表示/非表示（デフォルト: false）
 */
export const HintPopup: React.FC<HintPopupProps> = ({
  show,
  title,
  description,
  image,
  imageAlt = '',
  onClose,
  showCheckbox = false,
  checkboxValue = false,
  onCheckboxChange,
  checkboxLabel = '次回から表示しない',
  closeButtonText = '閉じる',
  maxWidth = '600px',
  onExecute,
  executeButtonText = '適用',
  showExecuteButton = false
}) => {
  if (!show) return null;

  return (
    <>
      {/* 背景オーバーレイ */}
      <div
        className="fixed inset-0"
        style={{ zIndex: 9999, backgroundColor: 'rgba(17, 24, 39, 0.5)' }}
        onClick={onClose}
      />

      {/* モーダルコンテンツ */}
      <div className="fixed inset-0 flex items-center justify-center p-4" style={{ zIndex: 10000 }}>
        <div
          className="relative bg-white rounded-lg shadow-xl w-full"
          style={{ maxWidth }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* コンテンツ領域 */}
          <div className="p-6">
            {/* タイトル（任意） */}
            {title && (
              <h4 className="text-xl font-bold text-gray-800 mb-4">{title}</h4>
            )}

            {/* 説明文（任意） */}
            {description && (
              <div className="space-y-3 text-sm text-gray-700">
                {description}
              </div>
            )}

            {/* 画像（任意） */}
            {image && (
              <img
                src={image}
                alt={imageAlt}
                className="w-full h-auto"
              />
            )}
          </div>

          {/* フッター */}
          <div
            className="bg-gray-50 px-6 rounded-b-lg"
            style={{ paddingTop: '16px', paddingBottom: '16px' }}
          >
            <div className="flex flex-col items-center gap-3">
              {/* チェックボックス（条件付き表示） */}
              {showCheckbox && onCheckboxChange && (
                <label
                  className="flex items-center cursor-pointer"
                  style={{ gap: '16px' }}
                >
                  <span className="text-sm text-gray-700">{checkboxLabel}</span>
                  <input
                    type="checkbox"
                    checked={checkboxValue}
                    onChange={(e) => onCheckboxChange(e.target.checked)}
                  />
                </label>
              )}

              {/* ボタン表示：実行ボタンありの場合は2ボタン、なしの場合は1ボタン */}
              {showExecuteButton && onExecute ? (
                <div className="flex gap-3 justify-center" style={{ width: '100%', maxWidth: '400px' }}>
                  <button
                    type="button"
                    onClick={onClose}
                    className="py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 transition-colors"
                    style={{ flex: 1 }}
                  >
                    キャンセル
                  </button>
                  <button
                    type="button"
                    onClick={onExecute}
                    className="py-2 bg-orange-500 text-white rounded hover:bg-orange-600 transition-colors"
                    style={{ flex: 1 }}
                  >
                    {executeButtonText}
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={onClose}
                  className="py-2 bg-orange-500 text-white rounded hover:bg-orange-600 transition-colors"
                  style={{ width: '200px' }}
                >
                  {closeButtonText}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
